import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Typography,
  Space,
  Row,
  Col,
  Alert,
  Descriptions,
  Tag,
  Divider,
  Statistic,
  ConfigProvider,
} from "antd";
import {
  ClockCircleOutlined,
  CrownOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  PhoneOutlined,
  MailOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "../utils/axiosConfig"; // استخدام axios المُعد بالتكوينات الصحيحة
import "./SubscriptionExpired.css";
import arEG from "antd/lib/locale/ar_EG";
import logger from "../utils/logger";

const { Title, Paragraph, Text } = Typography;

const SubscriptionExpired = () => {
  const navigate = useNavigate();
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [plans, setPlans] = useState([]);
  const [systemSettings, setSystemSettings] = useState({
    default_currency: "IQD",
    support_email: "almarjeapro@gmail.com",
    support_phone: "009647889702444",
    system_name: "نظام إدارة مكاتب المحاماة",
  });
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [showPlans, setShowPlans] = useState(false);

  // Helper function to safely parse features
  const parseFeatures = (features) => {
    if (!features) return [];

    // إذا كانت مصفوفة بالفعل
    if (Array.isArray(features)) return features;

    // محاولة parse JSON
    try {
      const parsed = JSON.parse(features);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      // إذا فشل parse، نعتبره نص واحد
      return [features];
    }
  };

  // Helper function to format date in Gregorian (ميلادي) with English numbers
  const formatDate = (dateString) => {
    if (!dateString) return "-";

    const date = new Date(dateString);

    // التنسيق بالميلادي بالأرقام الإنجليزية فقط (DD/MM/YYYY)
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  };

  // Helper function to get currency symbol
  const getCurrencySymbol = () => {
    const currencyMap = {
      IQD: "د.ع",
      SAR: "ر.س",
      USD: "$",
      EUR: "€",
      GBP: "£",
    };
    return (
      currencyMap[systemSettings.default_currency] ||
      systemSettings.default_currency
    );
  };

  const fetchSystemSettings = async () => {
    try {
      const response = await axios.get("/subscription/settings");

      if (response.data.success) {
        setSystemSettings((prev) => ({
          ...prev,
          ...response.data.data,
        }));
        logger.debug("✅ System settings loaded:", response.data.data);
      }
    } catch (error) {
      logger.error("❌ Error fetching system settings:", error);
      // استخدام القيم الافتراضية في حالة الخطأ
    }
  };

  useEffect(() => {
    fetchSubscriptionInfo();
    fetchPlans();
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      // استخدام axios من axiosConfig (يحتوي على baseURL)
      const response = await axios.get("/subscription/status");

      logger.debug("📊 Subscription Status Response:", response.data);

      if (response.data.success) {
        const data = response.data.data;
        setSubscriptionInfo(data);

        // ⚠️ التحقق: إذا كان الاشتراك نشط، نوجه للصفحة الرئيسية
        if (data.subscription?.isActive === true) {
          logger.debug("✅ Subscription is active! Redirecting to dashboard...");
          // تحديث localStorage
          localStorage.removeItem("subscriptionExpired");
          localStorage.setItem(
            "subscription",
            JSON.stringify(data.subscription)
          );
          // إعادة توجيه للصفحة الرئيسية
          window.location.href = "/dashboard";
          return;
        }

        // تحديث localStorage
        if (data.subscription) {
          localStorage.setItem(
            "subscription",
            JSON.stringify(data.subscription)
          );
          logger.debug("📊 Subscription info updated:", data.subscription);
        }
      }
    } catch (error) {
      logger.error("❌ Error fetching subscription info:", error);
      if (error.response) {
        logger.error("Response data:", error.response.data);
        logger.error("Response status:", error.response.status);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      // استخدام axios من axiosConfig (يحتوي على baseURL)
      const response = await axios.get("/subscription/plans");

      logger.debug("📋 Plans Response:", response.data);

      if (response.data.success) {
        setPlans(response.data.data);
        logger.debug("✅ Plans loaded:", response.data.data.length);
      }
    } catch (error) {
      logger.error("❌ Error fetching plans:", error);
      if (error.response) {
        logger.error("Response data:", error.response.data);
      }
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(true);
    try {
      // إعادة توجيه مباشرة لصفحة checkout مع معلومات الخطة في الـ state
      navigate("/checkout", { state: { planId, action: "upgrade" } });
    } catch (error) {
      logger.error("Error upgrading:", error);
      alert("حدث خطأ في الترقية. يرجى المحاولة مرة أخرى.");
    } finally {
      setUpgrading(false);
    }
  };

  const handleRenew = async () => {
    setUpgrading(true);
    try {
      // إعادة توجيه مباشرة لصفحة checkout للتجديد عبر الـ state
      navigate("/checkout", { state: { action: "renew" } });
    } catch (error) {
      logger.error("Error renewing:", error);
      alert("حدث خطأ في التجديد. يرجى المحاولة مرة أخرى.");
    } finally {
      setUpgrading(false);
    }
  };

  const getStatusInfo = () => {
    if (!subscriptionInfo) return null;

    const { subscription, tenant } = subscriptionInfo;

    // Logger debug للتشخيص
    logger.debug("🔍 SubscriptionExpired - getStatusInfo:", {
      subscription,
      tenant,
      requiresAction: subscription?.requiresAction,
      isActive: subscription?.isActive,
    });

    // Handle case when requiresAction is null but trial has ended
    if (subscription.requiresAction === null && subscription.trialEndsAt) {
      const trialEndDate = new Date(subscription.trialEndsAt);
      const now = new Date();

      if (now > trialEndDate) {
        // Trial has ended, set requiresAction to "subscribe"
        subscription.requiresAction = "subscribe";
      }
    }

    // Handle case when requiresAction is null but subscription has ended
    if (
      subscription.requiresAction === null &&
      subscription.subscriptionEndsAt
    ) {
      const subscriptionEndDate = new Date(subscription.subscriptionEndsAt);
      const now = new Date();

      if (now > subscriptionEndDate) {
        // Subscription has ended, set requiresAction to "renew"
        subscription.requiresAction = "renew";
      }
    }

    // Handle case when requiresAction is null but tenant status indicates expired
    if (subscription.requiresAction === null && tenant?.status === "expired") {
      subscription.requiresAction = "renew";
    }

    // Handle case when requiresAction is null but tenant status indicates suspended
    if (
      subscription.requiresAction === null &&
      tenant?.status === "suspended"
    ) {
      subscription.requiresAction = "contact_support";
    }

    if (subscription.requiresAction === "subscribe") {
      return {
        icon: <CrownOutlined style={{ fontSize: 64, color: "#faad14" }} />,
        title: "انتهت فترتك التجريبية",
        description:
          "شكراً لتجربة نظامنا! للاستمرار في استخدام جميع المميزات، يرجى الاشتراك في إحدى الخطط.",
        type: "warning",
        action: "subscribe",
      };
    } else if (subscription.requiresAction === "payment") {
      const isPendingConfirmation =
        tenant?.status === "pending_payment" ||
        subscription?.status === "pending_payment";
      return {
        icon: (
          <ClockCircleOutlined style={{ fontSize: 64, color: "#faad14" }} />
        ),
        title: isPendingConfirmation
          ? "بانتظار تأكيد الإدارة"
          : "في انتظار الدفع",
        description: isPendingConfirmation
          ? "نشكرك على إتمام عملية الدفع. اشتراكك الآن بانتظار مراجعة وتأكيد الإدارة. سيتم تفعيل حسابك فور التحقق."
          : "تم إنشاء حسابك بنجاح! يرجى إتمام عملية الدفع لتفعيل اشتراكك والوصول إلى جميع ميزات النظام.",
        type: "warning",
        action: "payment",
      };
    } else if (subscription.requiresAction === "renew") {
      return {
        icon: <WarningOutlined style={{ fontSize: 64, color: "#ff4d4f" }} />,
        title: "انتهى اشتراكك",
        description:
          "اشتراكك في النظام قد انتهى. يرجى تجديد الاشتراك للاستمرار في الوصول إلى حسابك وبياناتك.",
        type: "error",
        action: "renew",
      };
    } else if (subscription.requiresAction === "contact_support") {
      return {
        icon: <PhoneOutlined style={{ fontSize: 64, color: "#1890ff" }} />,
        title: "تم تعليق حسابك",
        description:
          "تم تعليق حسابك مؤقتاً. يرجى التواصل مع الدعم الفني لحل المشكلة.",
        type: "info",
        action: "contact",
      };
    }

    return null;
  };

  if (loading) {
    return (
      <div className="subscription-expired-container">
        <Card loading />
      </div>
    );
  }

  const statusInfo = getStatusInfo();

  // إذا لا يوجد statusInfo، نعرض رسالة بسيطة
  if (!statusInfo) {
    logger.warn("⚠️ No statusInfo available:", {
      subscriptionInfo,
      subscription: subscriptionInfo?.subscription,
    });

    // إذا كانت البيانات موجودة لكن لا يوجد requiresAction، معناها الاشتراك نشط!
    if (subscriptionInfo?.subscription?.isActive === true) {
      return (
        <div className="subscription-expired-container">
          <Row
            justify="center"
            align="middle"
            style={{ minHeight: "100vh", padding: "20px" }}
          >
            <Col xs={24} sm={22} md={20} lg={18} xl={16}>
              <Card
                className="subscription-expired-card"
                bordered={false}
                style={{
                  textAlign: "center",
                  borderRadius: 16,
                  boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
                }}
              >
                <Space
                  direction="vertical"
                  size="large"
                  style={{ width: "100%" }}
                >
                  <div style={{ marginTop: 20 }}>
                    <CheckCircleOutlined
                      style={{ fontSize: 64, color: "#52c41a" }}
                    />
                  </div>
                  <Title level={2}>اشتراكك نشط!</Title>
                  <Paragraph style={{ fontSize: 16, color: "#666" }}>
                    سيتم إعادة توجيهك للصفحة الرئيسية...
                  </Paragraph>
                  <Button
                    type="primary"
                    size="large"
                    onClick={() => navigate("/dashboard")}
                  >
                    الانتقال للوحة التحكم
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      );
    }

    // رسالة التحميل
    return (
      <div className="subscription-expired-container">
        <Row
          justify="center"
          align="middle"
          style={{ minHeight: "100vh", padding: "20px" }}
        >
          <Col xs={24} sm={22} md={20} lg={18} xl={16}>
            <Card
              className="subscription-expired-card"
              bordered={false}
              style={{
                textAlign: "center",
                borderRadius: 16,
                boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
              }}
            >
              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                <div style={{ marginTop: 20 }}>
                  <CheckCircleOutlined
                    style={{ fontSize: 64, color: "#52c41a" }}
                  />
                </div>
                <Title level={2}>جاري التحميل...</Title>
                <Paragraph style={{ fontSize: 16, color: "#666" }}>
                  يتم تحديث معلومات الاشتراك
                </Paragraph>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <ConfigProvider direction="rtl" locale={arEG}>
      <div className="subscription-expired-container">
        <Row
          justify="center"
          align="middle"
          style={{ minHeight: "100vh", padding: "20px" }}
        >
          <Col xs={24} sm={22} md={20} lg={18} xl={16}>
            {/* البطاقة الرئيسية */}
            <Card
              className="subscription-expired-card"
              bordered={false}
              style={{
                textAlign: "center",
                borderRadius: 16,
                boxShadow: "0 4px 24px rgba(0,0,0,0.1)",
              }}
            >
              <Space
                direction="vertical"
                size="large"
                style={{ width: "100%" }}
              >
                {/* الأيقونة */}
                <div style={{ marginTop: 20 }}>{statusInfo.icon}</div>

                {/* العنوان */}
                <Title level={2} style={{ marginBottom: 0 }}>
                  {statusInfo.title}
                </Title>

                {/* الوصف */}
                <Paragraph
                  style={{
                    fontSize: 16,
                    color: "#666",
                    maxWidth: 600,
                    margin: "0 auto",
                  }}
                >
                  {statusInfo.description}
                </Paragraph>

                {/* معلومات الحساب */}
                {subscriptionInfo && (
                  <Card
                    style={{
                      background: "#f5f5f5",
                      borderRadius: 8,
                      textAlign: "right",
                    }}
                  >
                    <Descriptions column={1} size="small">
                      <Descriptions.Item label="اسم المكتب">
                        <Text strong>{subscriptionInfo.tenant.name}</Text>
                      </Descriptions.Item>
                      <Descriptions.Item label="الخطة الحالية">
                        <Tag color="blue">{subscriptionInfo.plan.name}</Tag>
                      </Descriptions.Item>
                      {subscriptionInfo.subscription.trialEndsAt && (
                        <Descriptions.Item label="انتهت الفترة التجريبية">
                          {formatDate(
                            subscriptionInfo.subscription.trialEndsAt
                          )}
                        </Descriptions.Item>
                      )}
                      {subscriptionInfo.subscription.subscriptionEndsAt && (
                        <Descriptions.Item label="انتهى الاشتراك">
                          {formatDate(
                            subscriptionInfo.subscription.subscriptionEndsAt
                          )}
                        </Descriptions.Item>
                      )}
                    </Descriptions>
                  </Card>
                )}

                <Divider />

                {/* الأزرار حسب الحالة */}
                {statusInfo.action === "subscribe" && (
                  <div>
                    <Title level={4}>اختر الخطة المناسبة لك</Title>
                    <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
                      {plans.map((plan) => (
                        <Col xs={24} sm={12} md={8} key={plan.id}>
                          <Card
                            hoverable
                            className="plan-card"
                            style={{ height: "100%" }}
                          >
                            <Space
                              direction="vertical"
                              size="middle"
                              style={{ width: "100%" }}
                            >
                              <Title level={4} style={{ marginBottom: 0 }}>
                                {plan.name_ar || plan.name}
                              </Title>
                              <div>
                                <Statistic
                                  value={plan.price}
                                  suffix={
                                    plan.billing_period === "monthly"
                                      ? `${getCurrencySymbol()} / شهر`
                                      : `${getCurrencySymbol()} / سنة`
                                  }
                                  valueStyle={{
                                    color: "#1890ff",
                                    fontSize: 24,
                                  }}
                                />
                              </div>{" "}
                              {plan.features && (
                                <div style={{ textAlign: "right" }}>
                                  {parseFeatures(plan.features).map(
                                    (feature, idx) => (
                                      <div
                                        key={idx}
                                        style={{ marginBottom: 8 }}
                                      >
                                        <CheckCircleOutlined
                                          style={{
                                            color: "#52c41a",
                                            marginLeft: 8,
                                          }}
                                        />
                                        <Text>{feature}</Text>
                                      </div>
                                    )
                                  )}
                                </div>
                              )}
                              <Button
                                type="primary"
                                size="large"
                                block
                                loading={upgrading}
                                onClick={() => handleUpgrade(plan.id)}
                              >
                                اختر هذه الخطة
                              </Button>
                            </Space>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}

                {statusInfo.action === "payment" && (
                  <div>
                    <Title level={4}>إتمام عملية الدفع</Title>
                    <Paragraph style={{ fontSize: 16, color: "#666" }}>
                      يرجى إتمام عملية الدفع لتفعيل حسابك والوصول إلى جميع ميزات
                      النظام.
                    </Paragraph>
                    <Space>
                      <Button
                        type="primary"
                        size="large"
                        icon={<CheckCircleOutlined />}
                        loading={upgrading}
                        onClick={() => navigate("/checkout")}
                      >
                        إتمام الدفع
                      </Button>
                      <Button size="large" onClick={() => setShowPlans(true)}>
                        تغيير الخطة
                      </Button>
                    </Space>

                    {showPlans && (
                      <div style={{ marginTop: 24 }}>
                        <Title level={4}>اختر الخطة المناسبة لك</Title>
                        <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
                          {plans.map((plan) => (
                            <Col xs={24} sm={12} md={8} key={plan.id}>
                              <Card
                                hoverable
                                className="plan-card"
                                style={{ height: "100%" }}
                              >
                                <Space
                                  direction="vertical"
                                  size="middle"
                                  style={{ width: "100%" }}
                                >
                                  <Title level={4} style={{ marginBottom: 0 }}>
                                    {plan.name_ar || plan.name}
                                  </Title>
                                  <div>
                                    <Statistic
                                      value={plan.price}
                                      suffix={
                                        plan.billing_period === "monthly"
                                          ? `${getCurrencySymbol()} / شهر`
                                          : `${getCurrencySymbol()} / سنة`
                                      }
                                      valueStyle={{
                                        color: "#1890ff",
                                        fontSize: 24,
                                      }}
                                    />
                                  </div>
                                  {plan.features && (
                                    <div style={{ textAlign: "right" }}>
                                      {parseFeatures(plan.features).map(
                                        (feature, idx) => (
                                          <div
                                            key={idx}
                                            style={{ marginBottom: 8 }}
                                          >
                                            <CheckCircleOutlined
                                              style={{
                                                color: "#52c41a",
                                                marginLeft: 8,
                                              }}
                                            />
                                            <Text>{feature}</Text>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}
                                  <Button
                                    type="primary"
                                    size="large"
                                    block
                                    loading={upgrading}
                                    onClick={() => handleUpgrade(plan.id)}
                                  >
                                    اختر هذه الخطة
                                  </Button>
                                </Space>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                      </div>
                    )}
                  </div>
                )}

                {statusInfo.action === "renew" && (
                  <div>
                    {!showPlans ? (
                      <Space>
                        <Button
                          type="primary"
                          size="large"
                          icon={<CheckCircleOutlined />}
                          loading={upgrading}
                          onClick={handleRenew}
                        >
                          تجديد الاشتراك
                        </Button>
                        <Button size="large" onClick={() => setShowPlans(true)}>
                          تغيير الخطة
                        </Button>
                      </Space>
                    ) : (
                      <div>
                        <Title level={4}>اختر الخطة المناسبة لك</Title>
                        <Row gutter={[16, 16]} style={{ marginTop: 20 }}>
                          {plans.map((plan) => (
                            <Col xs={24} sm={12} md={8} key={plan.id}>
                              <Card
                                hoverable
                                className="plan-card"
                                style={{ height: "100%" }}
                              >
                                <Space
                                  direction="vertical"
                                  size="middle"
                                  style={{ width: "100%" }}
                                >
                                  <Title level={4} style={{ marginBottom: 0 }}>
                                    {plan.name_ar || plan.name}
                                  </Title>

                                  <div>
                                    <Statistic
                                      value={plan.price}
                                      suffix={
                                        plan.billing_period === "monthly"
                                          ? `${getCurrencySymbol()} / شهر`
                                          : `${getCurrencySymbol()} / سنة`
                                      }
                                      valueStyle={{
                                        color: "#1890ff",
                                        fontSize: 24,
                                      }}
                                    />
                                  </div>

                                  {plan.features && (
                                    <div style={{ textAlign: "right" }}>
                                      {parseFeatures(plan.features).map(
                                        (feature, idx) => (
                                          <div
                                            key={idx}
                                            style={{ marginBottom: 8 }}
                                          >
                                            <CheckCircleOutlined
                                              style={{
                                                color: "#52c41a",
                                                marginLeft: 8,
                                              }}
                                            />
                                            <Text>{feature}</Text>
                                          </div>
                                        )
                                      )}
                                    </div>
                                  )}

                                  <Button
                                    type="primary"
                                    size="large"
                                    block
                                    loading={upgrading}
                                    onClick={() => handleUpgrade(plan.id)}
                                  >
                                    اختر هذه الخطة
                                  </Button>
                                </Space>
                              </Card>
                            </Col>
                          ))}
                        </Row>
                        <Button
                          style={{ marginTop: 16 }}
                          onClick={() => setShowPlans(false)}
                        >
                          رجوع
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {statusInfo.action === "contact" && (
                  <Space direction="vertical" size="middle">
                    <Alert
                      message="تواصل معنا"
                      description="للمساعدة في حل المشكلة، يرجى التواصل مع فريق الدعم الفني"
                      type="info"
                      showIcon
                    />
                    <Space>
                      <Button
                        type="primary"
                        size="large"
                        style={{
                          backgroundColor: "#25D366",
                          borderColor: "#25D366",
                        }}
                        icon={
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            fill="currentColor"
                            className="bi bi-whatsapp"
                            viewBox="0 0 16 16"
                          >
                            <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007-.133 0-.347.05-.529.248-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
                          </svg>
                        }
                        href={`https://wa.me/${systemSettings.support_phone?.replace(
                          /[^0-9]/g,
                          ""
                        )}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        تواصل عبر الواتساب
                      </Button>
                      <Button
                        size="large"
                        icon={<MailOutlined />}
                        href={`mailto:${systemSettings.support_email}`}
                      >
                        راسلنا
                      </Button>
                    </Space>
                  </Space>
                )}

                <Divider />

                {/* معلومات إضافية */}
                <div style={{ marginTop: 20 }}>
                  <Paragraph type="secondary" style={{ fontSize: 14 }}>
                    <ClockCircleOutlined style={{ marginLeft: 5 }} />
                    بياناتك آمنة ومحفوظة. بعد تجديد الاشتراك، يمكنك الوصول إلى
                    جميع بياناتك فوراً.
                  </Paragraph>

                  <Paragraph type="secondary" style={{ fontSize: 14 }}>
                    هل تحتاج مساعدة؟ تواصل معنا على:
                    <a
                      href={`https://wa.me/${systemSettings.support_phone?.replace(
                        /[^0-9]/g,
                        ""
                      )}`}
                      style={{
                        marginRight: 10,
                        marginLeft: 10,
                        color: "#25D366",
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="currentColor"
                        className="bi bi-whatsapp"
                        viewBox="0 0 16 16"
                        style={{ marginLeft: 5 }}
                      >
                        <path d="M13.601 2.326A7.854 7.854 0 0 0 7.994 0C3.627 0 .068 3.558.064 7.926c0 1.399.366 2.76 1.057 3.965L0 16l4.204-1.102a7.933 7.933 0 0 0 3.79.965h.004c4.368 0 7.926-3.558 7.93-7.93A7.898 7.898 0 0 0 13.6 2.326zM7.994 14.521a6.573 6.573 0 0 1-3.356-.92l-.24-.144-2.494.654.666-2.433-.156-.251a6.56 6.56 0 0 1-1.007-3.505c0-3.626 2.957-6.584 6.591-6.584a6.56 6.56 0 0 1 4.66 1.931 6.557 6.557 0 0 1 1.928 4.66c-.004 3.639-2.961 6.592-6.592 6.592zm3.615-4.934c-.197-.099-1.17-.578-1.353-.646-.182-.065-.315-.099-.445.099-.133.197-.513.646-.627.775-.114.133-.232.148-.43.05-.197-.1-.836-.308-1.592-.985-.59-.525-.985-1.175-1.103-1.372-.114-.198-.011-.304.088-.403.087-.088.197-.232.296-.346.1-.114.133-.198.198-.33.065-.134.034-.248-.015-.347-.05-.099-.445-1.076-.612-1.47-.16-.389-.323-.335-.445-.34-.114-.007-.247-.007-.38-.007-.133 0-.347.05-.529.248-.182.198-.691.677-.691 1.654 0 .977.71 1.916.81 2.049.098.133 1.394 2.132 3.383 2.992.47.205.84.326 1.129.418.475.152.904.129 1.246.08.38-.058 1.171-.48 1.338-.943.164-.464.164-.86.114-.943-.049-.084-.182-.133-.38-.232z" />
                      </svg>
                      {systemSettings.support_phone}
                    </a>
                    أو
                    <a
                      href={`mailto:${systemSettings.support_email}`}
                      style={{ marginRight: 10 }}
                    >
                      {systemSettings.support_email}
                    </a>
                  </Paragraph>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>
    </ConfigProvider>
  );
};

export default SubscriptionExpired;
